import React, {useEffect, useMemo} from 'react';
import {useHeader} from "../../contexts/HeaderContext";
import {useAuth} from "../../contexts/AuthContext";

const DocumentationPage = () => {
    const {setTitle} = useHeader();
    const {isAdmin} = useAuth();

    useEffect(() => {
        setTitle("Documentation");
    }, [setTitle]);

    const sections = useMemo(() => {
        const base = [
            {id: 'accueil', title: "Vue d'ensemble"},
            {id: 'dossiers', title: 'Gestion des dossiers'},
            {id: 'dashboard', title: 'Tableau de bord'},
            {id: 'recherche', title: 'Recherche globale'},
            {id: 'statuts', title: 'Statuts des dossiers'},
        ];
        if (isAdmin) {
            base.push(
                {id: 'utilisateurs', title: 'Gestion des utilisateurs'},
                {id: '2fa', title: 'Authentification à deux facteurs (2FA)'},
            );
        }
        return base;
    }, [isAdmin]);

    return (
        <div className="min-h-screen-fit animate-in fade-in duration-300 bg-gray-50 p-4 space-y-6">

            {/* Sommaire */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Sommaire</h2>
                <nav className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sections.map((section, index) => (
                        <a
                            key={section.id}
                            href={`#section-${section.id}`}
                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-emerald-50 text-sm text-gray-700 hover:text-emerald-700 transition-colors"
                        >
              <span
                  className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                {index + 1}
              </span>
                            {section.title}
                        </a>
                    ))}
                </nav>
            </div>

            {/* 1. Vue d'ensemble */}
            <div id="section-accueil" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span
                className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">1</span>
                    Vue d'ensemble
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    L'Extranet Groupe Surplus est une plateforme de gestion des dossiers de véhicules. Elle permet le
                    suivi des
                    enlèvements,
                    le pilotage de la performance et la gestion des dossiers en temps réel.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-100 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Rôles utilisateurs</h3>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li><span className="font-medium text-emerald-700">Administrateur (ADMIN)</span> — Accès
                                complet à
                                toutes les données et fonctionnalités, gestion des utilisateurs, configuration du 2FA.
                            </li>
                            <li><span className="font-medium text-blue-600">Gestionnaire (GEST)</span> — Accès limité
                                aux dossiers
                                et statistiques de ses partenaires associés.
                            </li>
                        </ul>
                    </div>
                    <div className="border border-gray-100 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Accès rapides</h3>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li><span className="font-medium">Accueil</span> — Synthèse de performance, derniers
                                dossiers, statuts.
                            </li>
                            <li><span className="font-medium">Dossiers</span> — Liste complète avec filtres avancés.
                            </li>
                            <li><span className="font-medium">Tableau de bord</span> — Indicateurs et graphiques
                                détaillés.
                            </li>
                            <li><span className="font-medium">Paramétrage</span> — Gestion des utilisateurs (admin).
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* 2. Gestion des dossiers */}
            <div id="section-dossiers" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span
                className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">2</span>
                    Gestion des dossiers
                </h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Liste des dossiers</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            La page <span className="font-medium">Dossiers</span> affiche l'ensemble des dossiers sous
                            forme de
                            tableau paginé (25 par page).
                            Les gestionnaires ne voient que les dossiers liés à leurs partenaires.
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                            <p className="font-medium text-gray-900 mb-1">Filtres avancés disponibles :</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                <span>Période (début / fin)</span>
                                <span>Référence dossier</span>
                                <span>Propriétaire</span>
                                <span>Immatriculation</span>
                                <span>Marque / Modèle</span>
                                <span>Référence partenaire</span>
                                <span>Partenaire</span>
                                <span>Statut</span>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                Les filtres et la recherche globale sont mutuellement exclusifs : utiliser l'un
                                réinitialise l'autre.
                            </p>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Consultation d'un dossier</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            Cliquez sur un dossier pour accéder à sa fiche détaillée. L'en-tête affiche le véhicule
                            (marque, modèle,
                            immatriculation),
                            la source et le partenaire.
                        </p>
                        <p className="text-sm text-gray-600 mb-2">
                            Le panneau latéral droit affiche le statut actuel, la checklist de complétion et les
                            informations clés
                            du dossier
                            (date de création, date de cession, convention).
                        </p>
                    </div>

                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Onglets du dossier</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            L'onglet <span className="font-medium">Gestion du dossier</span> contient cinq sous-onglets
                            avec
                            indicateur de complétion :
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                            {[
                                {name: 'Sinistre', desc: "Informations du sinistre et de l'immatriculation"},
                                {name: 'Propriétaire', desc: 'Coordonnées du propriétaire du véhicule'},
                                {name: 'Dépositaire', desc: 'Coordonnées du dépositaire'},
                                {name: 'Enlèvement', desc: "Dates et détails de l'enlèvement"},
                                {name: 'Frais', desc: 'Frais de remorquage, gardiennage et autres'},
                            ].map((tab) => (
                                <div key={tab.name} className="border border-gray-100 rounded-lg p-3 text-center">
                                    <p className="font-medium text-sm text-gray-900">{tab.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">{tab.desc}</p>
                                </div>
                            ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-3">
                            L'onglet <span className="font-medium">Fiche véhicule</span> affiche les données techniques
                            en lecture
                            seule :
                            photos, identification (genre, VIN, marque), caractéristiques (énergie, cylindrée), état et
                            kilométrage.
                        </p>
                    </div>
                </div>
            </div>

            {/* 3. Tableau de bord */}
            <div id="section-dashboard" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span
                className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">3</span>
                    Tableau de bord
                </h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Page d'accueil</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            La page d'accueil présente une vue synthétique avec :
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                            <li>La <span className="font-medium">synthèse de performance</span> : indicateurs VH à
                                enlever, VH en
                                cours, délai moyen.
                            </li>
                            <li>Les <span className="font-medium">dossiers par statut</span> : tuiles cliquables
                                filtrant la liste
                                par statut.
                            </li>
                            <li>Un aperçu des <span className="font-medium">indicateurs de suivi</span> (véhicules
                                enlevés par
                                mois).
                            </li>
                            <li>Les <span className="font-medium">5 derniers dossiers</span> avec accès rapide.</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2">Indicateurs détaillés</h3>
                        <p className="text-sm text-gray-600 mb-2">
                            La page <span className="font-medium">Tableau de bord</span> (accessible via le menu) offre
                            des
                            graphiques détaillés avec filtres :
                        </p>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                            <p className="font-medium text-gray-900 mb-1">Filtres disponibles :</p>
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-1">
                                <span>Source</span>
                                <span>Année</span>
                                <span>Mois</span>
                                <span>Statut</span>
                                <span>Fournisseur</span>
                                <span>Agence</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                            {[
                                {name: 'Véhicules enlevés', desc: 'Nombre de véhicules enlevés par mois (barres)'},
                                {
                                    name: 'Taux de service moyen',
                                    desc: 'Pourcentage moyen de véhicules enlevés dans les délais'
                                },
                                {name: 'Taux de service', desc: 'Évolution mensuelle du taux de service (courbe)'},
                                {
                                    name: 'Frais avancés',
                                    desc: 'Remorquage, gardiennage et autres frais par mois (barres groupées)'
                                },
                            ].map((chart) => (
                                <div key={chart.name} className="border border-gray-100 rounded-lg p-3">
                                    <p className="font-medium text-sm text-gray-900">{chart.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">{chart.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. Recherche globale */}
            <div id="section-recherche" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span
                className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">4</span>
                    Recherche globale
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    La barre de recherche dans l'en-tête permet de trouver rapidement un dossier. Le système détecte
                    automatiquement
                    le type de recherche en fonction du format saisi :
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="bg-gray-50">
                            <th className="text-left px-4 py-2 font-semibold text-gray-700">Format saisi</th>
                            <th className="text-left px-4 py-2 font-semibold text-gray-700">Champ recherché</th>
                            <th className="text-left px-4 py-2 font-semibold text-gray-700">Exemple</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        <tr>
                            <td className="px-4 py-2 text-gray-600"><code
                                className="bg-gray-100 px-1 rounded">XXX-123456</code>
                            </td>
                            <td className="px-4 py-2 text-gray-600">Source + Référence</td>
                            <td className="px-4 py-2 text-gray-500">SAU-000042</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Nom de source connu</td>
                            <td className="px-4 py-2 text-gray-600">Source (libellé)</td>
                            <td className="px-4 py-2 text-gray-500">SURPLUS AUTOS</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Deux mots ou plus</td>
                            <td className="px-4 py-2 text-gray-600">Marque + Modèle</td>
                            <td className="px-4 py-2 text-gray-500">PEUGEOT 308</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Texte simple</td>
                            <td className="px-4 py-2 text-gray-600">Tous les champs (immat., réf., marque, modèle)</td>
                            <td className="px-4 py-2 text-gray-500">AB-123-CD</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                    La recherche redirige automatiquement vers la liste des dossiers avec les filtres correspondants
                    appliqués.
                </p>
            </div>

            {/* 5. Statuts des dossiers */}
            <div id="section-statuts" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span
                className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">5</span>
                    Statuts des dossiers
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    Chaque dossier est associé à un statut reflétant l'étape en cours du processus d'enlèvement et de restitution du véhicule.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="bg-gray-50">
                            <th className="text-left px-4 py-2 font-semibold text-gray-700">Status</th>
                            <th className="text-left px-4 py-2 font-semibold text-gray-700">Définition</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Enlèvement annulé</td>
                            <td className="px-4 py-2 text-gray-600">L'enlèvement a été annulé. Dossier Clos.</td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Enlèvement en cours</td>
                            <td className="px-4 py-2 text-gray-600">L'enlèvement est pris en charge et sera réalisé
                                prochainement.
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Restitué au propriétaire</td>
                            <td className="px-4 py-2 text-gray-600">Le véhicule a été restitué au propriétaire. Dossier
                                Clos.
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Restitution en cours</td>
                            <td className="px-4 py-2 text-gray-600">Le véhicule doit être restitué au propriétaire au
                                lieu de dépôt convenu.
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Véhicule enlevé, livraison sur site en cours</td>
                            <td className="px-4 py-2 text-gray-600">L'enlèvement du véhicule a été réalisé. Il est en
                                transit sur un point de relais temporaire.
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Stockage sur site, Attente Mise à Disposition</td>
                            <td className="px-4 py-2 text-gray-600">Le véhicule se trouve physiquement sur un de nos
                                sites CVHU. Il est en attente d'une mise à disposition (cession).
                            </td>
                        </tr>
                        <tr>
                            <td className="px-4 py-2 text-gray-600">Véhicule pris en charge</td>
                            <td className="px-4 py-2 text-gray-600">La mise à disposition du véhicule a été effectuée.</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                    La recherche redirige automatiquement vers la liste des dossiers avec les filtres correspondants
                    appliqués.
                </p>
            </div>

            {isAdmin && (
                <>
                    {/* 6. Gestion des utilisateurs */}
                    <div id="section-utilisateurs" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span
                      className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">6</span>
                            Gestion des utilisateurs
                        </h2>
                        <p className="text-sm text-gray-600 mb-1">
                  <span
                      className="inline-block bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded">Admin uniquement</span>
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                            Accessible via <span className="font-medium">Paramétrage &gt; Utilisateurs</span> dans le
                            menu
                            latéral.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Liste des utilisateurs</h3>
                                <p className="text-sm text-gray-600">
                                    Le tableau affiche : nom, prénom, e-mail, nom d'utilisateur, rôle, partenaires
                                    associés, statut
                                    2FA et actions (modifier / supprimer).
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Création / Modification</h3>
                                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                                    <p className="font-medium text-gray-900 mb-1">Champs du formulaire :</p>
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li><span className="font-medium">Nom, Prénom</span> — obligatoires</li>
                                        <li><span className="font-medium">E-mail</span> — obligatoire, format validé
                                        </li>
                                        <li><span className="font-medium">Nom d'utilisateur</span> — obligatoire</li>
                                        <li><span className="font-medium">Mot de passe + Confirmation</span> —
                                            obligatoire à la création
                                            (min. 8 caractères), optionnel en modification
                                        </li>
                                        <li><span className="font-medium">Rôle</span> — Administrateur ou Gestionnaire
                                        </li>
                                        <li><span className="font-medium">Partenaires</span> — sélection multiple,
                                            uniquement pour le
                                            rôle Gestionnaire (obligatoire)
                                        </li>
                                        <li><span className="font-medium">2FA requis</span> — case à cocher, visible
                                            uniquement en
                                            modification par un admin
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Rôles</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <div className="border border-gray-100 rounded-lg p-3">
                                        <p className="font-medium text-sm text-emerald-700">Administrateur (ADMIN)</p>
                                        <ul className="text-xs text-gray-500 mt-1 space-y-0.5 list-disc list-inside">
                                            <li>Accès à tous les dossiers de toutes les sources</li>
                                            <li>Gestion complète des utilisateurs</li>
                                            <li>Configuration et réinitialisation du 2FA</li>
                                            <li>Accès aux indicateurs tous partenaires</li>
                                        </ul>
                                    </div>
                                    <div className="border border-gray-100 rounded-lg p-3">
                                        <p className="font-medium text-sm text-blue-600">Gestionnaire (GEST)</p>
                                        <ul className="text-xs text-gray-500 mt-1 space-y-0.5 list-disc list-inside">
                                            <li>Accès limité aux dossiers de ses partenaires</li>
                                            <li>Statistiques filtrées par ses partenaires</li>
                                            <li>Peut modifier son propre profil</li>
                                            <li>Pas d'accès au menu Paramétrage</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 7. 2FA */}
                    <div id="section-2fa" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-20">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span
                      className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">7</span>
                            Authentification à deux facteurs (2FA)
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Fonctionnement</h3>
                                <p className="text-sm text-gray-600">
                                    Le 2FA ajoute une couche de sécurité via un code temporaire (TOTP) généré par une
                                    application
                                    d'authentification (Google Authenticator, Authy, etc.). Lorsqu'il est activé,
                                    l'utilisateur doit
                                    saisir un code à 6 chiffres après son mot de passe à chaque connexion.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Activation par
                                    l'administrateur</h3>
                                <p className="text-sm text-gray-600">
                                    Un administrateur active le 2FA pour un utilisateur en cochant <span
                                    className="font-medium">2FA requis</span> dans
                                    le formulaire de modification de l'utilisateur. Au prochain login, l'utilisateur
                                    sera guidé dans
                                    la configuration.
                                </p>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Configuration initiale
                                    (première
                                    connexion)</h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Un assistant en 3 étapes guide l'utilisateur :
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    <div className="border border-gray-100 rounded-lg p-3">
                                        <div className="flex items-center gap-2 mb-1">
                          <span
                              className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">1</span>
                                            <p className="font-medium text-sm text-gray-900">QR Code</p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Scanner le QR code avec l'application d'authentification. La clé secrète est
                                            aussi affichée en
                                            texte pour saisie manuelle.
                                        </p>
                                    </div>
                                    <div className="border border-gray-100 rounded-lg p-3">
                                        <div className="flex items-center gap-2 mb-1">
                          <span
                              className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">2</span>
                                            <p className="font-medium text-sm text-gray-900">Vérification</p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Saisir le code à 6 chiffres généré par l'application pour confirmer la
                                            configuration.
                                        </p>
                                    </div>
                                    <div className="border border-gray-100 rounded-lg p-3">
                                        <div className="flex items-center gap-2 mb-1">
                          <span
                              className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">3</span>
                                            <p className="font-medium text-sm text-gray-900">Codes de secours</p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Noter les codes de secours affichés. Ils ne seront plus accessibles par la
                                            suite et permettent
                                            de se connecter en cas de perte de l'application.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Connexion avec 2FA</h3>
                                <p className="text-sm text-gray-600">
                                    Après la saisie du mot de passe, un écran demande le code à 6 chiffres.
                                    En cas de perte de l'application, le lien <span className="font-medium">Utiliser un code de secours</span> permet
                                    de s'authentifier avec un code de secours à usage unique.
                                </p>
                                <div
                                    className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                                    <span className="font-semibold">Attention :</span> le nombre de tentatives est
                                    limité à 5.
                                    Au-delà, la saisie est bloquée
                                    et l'utilisateur doit contacter un administrateur.
                                </div>
                            </div>

                            <div>
                                <h3 className="font-semibold text-gray-900 text-sm mb-2">Réinitialisation du 2FA</h3>
                                <p className="text-sm text-gray-600">
                                    Un administrateur peut réinitialiser le 2FA d'un utilisateur depuis son formulaire
                                    de modification
                                    via le bouton <span className="font-medium">Réinitialiser le 2FA</span>.
                                    L'utilisateur devra
                                    reconfigurer
                                    son application d'authentification à la prochaine connexion.
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default DocumentationPage;
